import React from "react";
import { Link } from "react-router-dom";
import { MapPin, EyeOff, ShieldCheck } from "lucide-react";
import { setMapConsent, useMapConsent } from "@/lib/mapConsent";

const pill = "btn-pill inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors";

const MapPattern = () => (
  <svg aria-hidden="true" className="absolute inset-0 w-full h-full text-ink/[0.07]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 420">
    <g fill="none" stroke="currentColor" strokeLinecap="round">
      <path d="M-20 300 C 120 260, 200 330, 330 250 S 520 150, 640 190" strokeWidth="14" />
      <path d="M140 -20 C 170 120, 120 220, 210 440" strokeWidth="9" />
      <path d="M380 -20 L 420 140 L 520 440" strokeWidth="7" />
      <path d="M-20 90 L 640 130" strokeWidth="5" />
      <path d="M40 440 L 300 40" strokeWidth="4" />
      <path d="M460 60 C 520 120, 560 120, 640 100" strokeWidth="4" />
    </g>
    <g fill="currentColor">
      <rect x="220" y="150" width="70" height="40" rx="6" /><rect x="440" y="250" width="90" height="56" rx="6" />
      <rect x="40" y="170" width="60" height="70" rx="6" /><rect x="300" y="320" width="80" height="44" rx="6" />
    </g>
  </svg>
);

const Placeholder = ({ testId }) => (
  <div data-testid={`${testId}-consent`} className="absolute inset-0 flex items-center justify-center p-5 bg-elevated overflow-hidden">
    <MapPattern />
    <div className="relative w-full max-w-sm rounded-2xl border border-line bg-surface/90 backdrop-blur-md p-6 md:p-7 text-center shadow-[0_24px_60px_-32px_rgba(0,0,0,0.45)]">
      <span className="mx-auto w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center"><MapPin size={20} /></span>
      <p className="font-display font-bold text-lg text-ink mt-4">Karte von Google Maps</p>
      <p className="text-xs text-dim leading-relaxed mt-2">
        Zum Schutz Ihrer Daten laden wir die Karte erst nach Ihrem Klick. Dabei werden Daten wie Ihre IP-Adresse an Google übermittelt.
      </p>
      <button type="button" onClick={() => setMapConsent(true)} data-testid={`${testId}-consent-btn`}
        className={`${pill} bg-action text-action-ink hover:bg-action-hover px-6 py-3 mt-5`}>
        <MapPin size={15} /> Karte laden
      </button>
      <Link to="/datenschutz" data-testid={`${testId}-privacy-link`} className="block text-[11px] text-faint underline hover:text-ink mt-3">
        Mehr in der Datenschutzerklärung
      </Link>
    </div>
  </div>
);

export const ConsentMap = ({ src, title, testId }) => {
  const ok = useMapConsent();
  if (!ok) return <Placeholder testId={testId} />;
  return (
    <>
      <iframe title={title} src={src} data-testid={`${testId}-iframe`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen
        className="absolute inset-0 w-full h-full border-0 [filter:grayscale(0.35)_contrast(1.05)]" />
      <button type="button" onClick={() => setMapConsent(false)} data-testid={`${testId}-hide-btn`}
        className={`${pill} absolute top-3 right-3 bg-surface/90 backdrop-blur border border-line text-ink text-xs px-3.5 py-2 hover:border-silver`}>
        <EyeOff size={13} /> Karte ausblenden
      </button>
    </>
  );
};

export const MapConsentStatus = () => {
  const ok = useMapConsent();
  return (
    <div data-testid="maps-consent-status" className="rounded-xl border border-line bg-surface px-4 py-3.5 flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-dim inline-flex items-center gap-2">
        <ShieldCheck size={15} className="text-brand shrink-0" />
        {ok ? "Sie haben das Laden der Google-Karte für diesen Besuch erlaubt." : "Die Google-Karte ist derzeit nicht geladen – es besteht keine Verbindung zu Google."}
      </p>
      {ok && (
        <button type="button" onClick={() => setMapConsent(false)} data-testid="maps-consent-revoke-btn"
          className={`${pill} border border-line text-ink text-xs px-4 py-2 hover:border-silver`}>
          <EyeOff size={13} /> Einwilligung widerrufen
        </button>
      )}
    </div>
  );
};
